import React, { Fragment } from 'react';
import List from '@mui/material/List';
import Pagination from '@mui/material/Pagination';
import MapsJob from './MapsJob';
import { LocalAtRecord } from '../../../db';
import { Button, ListSubheader } from '@mui/material';
import _ from 'lodash';
import { jobsSortingFunction, robotsSortingFunction } from '../../../utils';
import { JobsFieldSet } from '@rogoag/airtable/crm';
import { SettingsAccessLevel } from '../../../types/types';
import { formatMapAcres, MapAcresSummary, summarizeMapAcres } from '../helpers/mapAcres';

const PAGE_SIZE = 50;

interface MapsScheduleCollectionProps {
  filterText: string;
  items: LocalAtRecord<JobsFieldSet>[];
  loadMissionMaps: (id: string) => Promise<void>;
  accessLevel: SettingsAccessLevel;
}

function getJobKey(job: LocalAtRecord) {
  const hasOperatorName = job.has('Operator Name') && !!job.get('Operator Name')?.[0];
  const operatorName = hasOperatorName ? job.get('Operator Name')[0] : 'Unassigned';
  const robot = job.has('Robot') && !!job.get('Robot') ? job.get('Robot') : '~No Robot';

  return `${robot} - ${operatorName}`;
}

function getScheduleKey(job: LocalAtRecord<JobsFieldSet>) {
  return `${job.get('Sched Date')?.toString() || 'No Schedule Date'}|${getJobKey(job)}`;
}

export default function MapsScheduleCollection(props: MapsScheduleCollectionProps) {
  const [page, setPage] = React.useState(1);

  // TODO leaving here for future filtering applications
  // const tokenArrayMatch = (filterText: string, value?: any[]) => {
  //   if (!value) return false;
  //   const searchName = value.map(v => v.toLowerCase()).join(' ');
  //   const tokens = filterText.split(' ');
  //   return tokens.every(token => searchName.includes(token));
  // }
  const tokenMatch = (filterText: string, name: string) => {
    if (!name) {
      return false;
    }

    const searchName = name.toLowerCase();
    const tokens = filterText.split(' ');

    return tokens.every((token) => searchName.includes(token));
  };

  const filterText = props.filterText?.trim();
  const isRogoJobId = filterText.startsWith('rec');

  const jobIdMatch = (filterText: string, job_id: string) => {
    return job_id.toLowerCase().includes(filterText);
  };

  const normalizedFilterText = filterText.toLowerCase();
  // TODO this is getting a bit inefficient, we need to come up with
  // a better way to filter items. For efficiency, we could probably
  // remove the point filtering options, those are likely not used
  // by anyone except me looking for specific types of jobs
  const allItems = props.items
    .filter(
      (sel) =>
        !filterText ||
        tokenMatch(normalizedFilterText, sel.get('Name') || '') ||
        tokenMatch(normalizedFilterText, sel.get('Robot') || '') ||
        // tokenArrayMatch(normalizedFilterText, sel.get('Operator Name')) ||
        (isRogoJobId && jobIdMatch(normalizedFilterText, sel.get('Rogo Job ID') || '')) ||
        (normalizedFilterText.includes('has:points') && sel.hasAttachments('Pts Shp')) ||
        (normalizedFilterText.includes('has:zones') && sel.hasAttachments('Sample Zones Shp')) ||
        (normalizedFilterText.includes('has:cores+zones') &&
          sel.hasAttachments('Sample Zones Shp') &&
          sel.hasAttachments('Pts Shp')),
    )
    .sort(jobsSortingFunction);

  const count = Math.ceil(allItems.length / PAGE_SIZE);
  const p = Math.min(page, count);

  const items = allItems.slice((p - 1) * PAGE_SIZE, p * PAGE_SIZE);

  const groupedJobs = items.reduce(
    (acc: Record<string, Record<string, LocalAtRecord<JobsFieldSet>[]>>, item: LocalAtRecord<JobsFieldSet>) => {
      const schedDate = item.get('Sched Date')?.toString() || 'No Schedule Date';

      if (!acc[schedDate]) {
        acc[schedDate] = {};
      }

      const jobKey = getJobKey(item);

      if (!acc[schedDate][jobKey]) {
        acc[schedDate][jobKey] = [];
      }

      acc[schedDate][jobKey].push(item);

      return acc;
    },
    {},
  );

  // Summed over the whole schedule rather than the filtered page, so a
  // person's total doesn't move with the search box or the pagination.
  const mapAcresBySchedule: Record<string, MapAcresSummary> = Object.fromEntries(
    Object.entries(_.groupBy(props.items, getScheduleKey)).map(([key, jobs]) => [key, summarizeMapAcres(jobs)]),
  );

  const groupedSchedDateKeys = Object.keys(groupedJobs).sort((a, b) => a.localeCompare(b));

  return (
    <React.Fragment>
      {isRogoJobId && items.length === 0 && (
        <Button variant={'outlined'} onClick={async () => await props.loadMissionMaps(props.filterText)}>
          Load From Airtable By ID
        </Button>
      )}
      {allItems.length > PAGE_SIZE ? (
        <Pagination
          count={count}
          page={p}
          variant="outlined"
          shape="rounded"
          onChange={(_e, p) => {
            setPage(p);
          }}
        />
      ) : null}
      <List dense>
        {groupedSchedDateKeys.map((schedDate: string) => (
          <Fragment key={schedDate}>
            <ListSubheader color="primary">{schedDate}</ListSubheader>
            {Object.keys(groupedJobs[schedDate])
              .sort(robotsSortingFunction)
              .map((robotOperatorKey: string) => (
                <Fragment key={robotOperatorKey}>
                  <ListSubheader>
                    {`${robotOperatorKey} — ${formatMapAcres(mapAcresBySchedule[`${schedDate}|${robotOperatorKey}`])}`}
                  </ListSubheader>
                  {groupedJobs[schedDate][robotOperatorKey]
                    .sort(jobsSortingFunction)
                    .map((job: LocalAtRecord<JobsFieldSet>) => (
                      <Fragment key={job.id}>
                        <MapsJob job={job} id={job.id} name={job.get('Name') || ''} {...props} />
                      </Fragment>
                    ))}
                </Fragment>
              ))}
          </Fragment>
        ))}
      </List>
    </React.Fragment>
  );
}
